export interface PaginationState {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface PaginationParams {
  PageNumber: number;
  PageSize: number;
}

export const paginationStateToParams = (pagination: PaginationState): PaginationParams => {
  return {
    PageNumber: pagination.currentPage,
    PageSize: pagination.pageSize,
  };
};

export const calculatePaginationPages = (
  currentPage: number,
  totalPages: number,
  maxVisiblePages: number = 5
): (number | string)[] => {
  const pages: (number | string)[] = [];

  if (totalPages <= maxVisiblePages) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  }

  pages.push(1);

  const startPage = Math.max(2, currentPage - 1);
  const endPage = Math.min(totalPages - 1, currentPage + 1);

  if (startPage > 2) {
    pages.push('...');
  }

    // Add pages around current
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  // Add ellipsis if needed
  if (endPage < totalPages - 1) {
    pages.push('...');
  }

  // Always show last page
  pages.push(totalPages);

  return pages;
};

/**
 * Check if pagination should be displayed
 */
export const shouldShowPagination = (totalPages: number, pageSize: number): boolean => {
  return totalPages > 1 && pageSize > 0;
};

/**
 * Get next page number
 */
export const getNextPage = (currentPage: number, totalPages: number): number | null => {
  return currentPage < totalPages ? currentPage + 1 : null;
};

/**
 * Get previous page number
 */
export const getPreviousPage = (currentPage: number): number | null => {
  return currentPage > 1 ? currentPage - 1 : null;
};

/**
 * Validate pagination params
 */
export const validatePaginationParams = (pageNumber: number, pageSize: number): boolean => {
  return pageNumber > 0 && pageSize > 0;
};

/**
 * Initialize default pagination state
 */
export const initializePaginationState = (pageSize: number = 5): PaginationState => {
  return {
    currentPage: 1,
    pageSize,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  };
};
